import React, { useEffect, useState } from 'react';
import { Alert, Spin } from 'antd';

import RecordBadge from '../common/RecordBadge';
import useTagRulePass from '../../hooks/useTagRulePass';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { fetchItemsByIds } from '../../services/itemsBulk';
import { normalizeId } from '../../utils/ids';
import { passState } from '../../utils/tagRules';

/** The items a finished pass lists by name, at most (the rest are counted). */
export const FULL_LISTED = 20;

/**
 * THE ITEMS AT THE LIMIT, named: record badges for the items (read through
 * the reader's own scope, `GET /api/items?ids=`), each on its card's tile,
 * and "And N more." for the rest of `count`. The pass's final report links
 * each to its item; "Stop applying by rule"'s question names them without
 * links (a confirm is no place to wander off).
 */
export function FullItemList({ items = [], count = 0, cardsById = {}, link = true, label = 'Items that already had 50 tags' }) {
	const listed = Array.isArray(items) ? items : [];
	if (!listed.length) return null;
	const unnamed = (Number(count) || 0) - listed.length;
	return (
		<div className="tag-rule-pass__full" data-testid="tag-rule-full">
			<ul className="tag-rule-pass__items" aria-label={label}>
				{listed.map(item => (
					<li key={normalizeId(item._id)}>
						<RecordBadge
							kind="item"
							size="xs"
							record={cardsById[normalizeId(item.card_id)] || null}
							title={item.displayValue || item.name || 'Untitled item'}
							to={link ? `/collections/${normalizeId(item.collection_id)}/items/${normalizeId(item._id)}` : undefined}
						/>
					</li>
				))}
			</ul>
			{unnamed > 0 ? <p className="tag-rule-pass__more">{`And ${unnamed.toLocaleString()} more.`}</p> : null}
		</div>
	);
}

/**
 * The items a stop's dry run or a pass names (`fullIds`, `progress.full`:
 * the first FULL_LISTED), read through the reader's scope and kept in the
 * order given. A failed read names none (they are still counted).
 */
export async function readFullItems(ids = []) {
	const wanted = (Array.isArray(ids) ? ids : []).map(normalizeId).filter(Boolean).slice(0, FULL_LISTED);
	if (!wanted.length) return [];
	try {
		const items = await fetchItemsByIds(wanted);
		const order = new Map(wanted.map((id, index) => [id, index]));
		return (Array.isArray(items) ? items : [])
			.filter(item => order.has(normalizeId(item?._id)))
			.sort((a, b) => order.get(normalizeId(a._id)) - order.get(normalizeId(b._id)));
	} catch (error) {
		return [];
	}
}

/**
 * AN AUTOMATIC TAG'S PASS, as one line (docs/plans/tags.md §3.4): the work a
 * save, a pause, a resume or "Stop applying by rule" started on the server,
 * followed while it runs (hooks/useTagRulePass) and then said as what it did
 * (utils/tagRules.js passState) — "Rule applied: tagged 42 items, took it off
 * 3." It sits under the tag's row in Settings › Tags, and in the rule's
 * editor while one is running.
 *
 * THE ITEMS AT THE LIMIT. An item already carrying 50 tags cannot take one
 * more: "Stop applying by rule" lists the ones that lost the tag (the pass's
 * `progress.full`, ids — its question named them first, from the dry run's
 * `fullIds`), and so does a sync for items at 50 automatic tags. Once the
 * pass is done, the first FULL_LISTED are named here (FullItemList), read
 * through the reader's own scope (`GET /api/items?ids=` — one the reader
 * cannot read is left out, and counted with the rest).
 *
 * While it runs the line says how far it has got — a share ("120 of 800
 * items checked") once the pass knows its total (keepr-api T6b).
 *
 * A pass that cannot run yet (`waiting`, e.g. the collection is archived) is
 * still followed, and says so; one whose world went away under it ends
 * `cancelled` ("It stopped before the end…").
 *
 * `onDismiss` puts a ✕ on a finished line.
 */
export default function TagRulePass({ tagId, pass: startedPass, onDismiss, className = '' }) {
	const { pass, gaveUp } = useTagRulePass(tagId, startedPass);
	const state = passState(pass);
	const cards = useOptionalCardDefinitions();
	const cardsById = cards?.cardsById || {};
	const [fullItems, setFullItems] = useState(null);

	const done = Boolean(state) && !state.running;
	const fullKey = done ? state.full.slice(0, FULL_LISTED).join(',') : '';
	useEffect(() => {
		if (!fullKey) { setFullItems(null); return undefined; }
		let active = true;
		readFullItems(fullKey.split(',')).then(items => { if (active) setFullItems(items); });
		return () => { active = false; };
	}, [fullKey]);

	if (!state) return null;
	const listed = Array.isArray(fullItems) ? fullItems : [];
	const message = (
		<span className="tag-rule-pass__text">
			{state.running && !state.waiting ? <Spin size="small" className="tag-rule-pass__spin" /> : null}
			{state.text}
			{state.running && gaveUp ? ' It is still going: come back later to see it finish.' : ''}
		</span>
	);
	const description = done && state.fullCount && listed.length
		? <FullItemList items={listed} count={state.fullCount} cardsById={cardsById} />
		: null;

	return (
		<Alert
			className={['tag-rule-pass', className].filter(Boolean).join(' ')}
			type={state.tone}
			showIcon={!state.running || Boolean(state.waiting)}
			message={message}
			description={description}
			closable={done && typeof onDismiss === 'function'}
			onClose={onDismiss}
			data-testid="tag-rule-pass"
			role="status"
		/>
	);
}
